import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DeveloperToolsTabs } from '@pages/DeveloperTools/DeveloperToolsContext';
import { PermissionsType } from '@shared/types/forms';

export enum Page {
  dashboard = 'dashboard',
  permissions = 'permissions',
  security = 'security',
  caseSettings = 'case-settings',
  scheduledReports = 'scheduled-reports',
  files = 'files',
  developerTools = 'developer-tools',
}

const caseAgnosticPages = [Page.security];

const kebabToCamelCase = (value: string) =>
  value.replace(/-(?<kebab>[a-z])/giu, (_, char: string) => char.toUpperCase());

const isPageKey = (value: string): value is keyof typeof Page =>
  Object.hasOwn(Page, value);

const mapPathnameToPageKey = (
  pathSegment?: string,
): keyof typeof Page | undefined => {
  if (!pathSegment) return undefined;
  const normalizedSegment = kebabToCamelCase(pathSegment);
  return isPageKey(normalizedSegment) ? normalizedSegment : undefined;
};

export const useAppLocation = () => {
  const location = useLocation();
  const urlPathname = location.pathname.split('/').slice(1);
  const pageKey = mapPathnameToPageKey(urlPathname[0]);
  const page = pageKey ? Page[pageKey] : Page.dashboard;
  const isOrganizationPathname = useMemo(
    () => urlPathname.includes(PermissionsType.Organization),
    [urlPathname],
  );
  const isApiKeysPathname = useMemo(
    () => urlPathname.includes(DeveloperToolsTabs.API_KEYS),
    [urlPathname],
  );
  const shouldShowOrgOrCase = !caseAgnosticPages.includes(page);

  return {
    location,
    urlPathname,
    page,
    isOrganizationPathname,
    isApiKeysPathname,
    shouldShowOrgOrCase,
  };
};

export default useAppLocation;
